import { z } from "zod";
import { InventoryItemSchema, PlayerWalletSchema } from "./entities.js";
import { BUILDING_FAMILIES, type BuildingFamily } from "./enums.js";

/** §6.2 production lifecycle — see core/production.ts for runtime state machine. */
export const PRODUCTION_STATES = ["queued", "active", "complete", "claimed"] as const;
export type ProductionState = (typeof PRODUCTION_STATES)[number];
export const ProductionStateSchema = z.enum(PRODUCTION_STATES);

const buildingFamilyEnum = z.enum(
  BUILDING_FAMILIES as readonly [BuildingFamily, ...BuildingFamily[]],
);

/** Single production_jobs row reflected as a wire-level DTO. */
export const ProductionJobSchema = z.object({
  id: z.string().uuid(),
  baseBuildingId: z.string().uuid(),
  family: buildingFamilyEnum,
  itemKey: z.string().min(1),
  startedAtMs: z.number().int().nonnegative(),
  durationMs: z.number().int().positive(),
  state: ProductionStateSchema,
  slotIndex: z.number().int().nonnegative(),
  enqueuedAtMs: z.number().int().nonnegative(),
});
export type ProductionJobDto = z.infer<typeof ProductionJobSchema>;

/** POST /api/production/start */
export const StartProductionRequestSchema = z.object({
  baseBuildingId: z.string().uuid(),
  itemKey: z.string().min(1),
});
export type StartProductionRequest = z.infer<typeof StartProductionRequestSchema>;

/** POST /api/production/speedup */
export const SpeedupProductionRequestSchema = z.object({
  jobId: z.string().uuid(),
});
export type SpeedupProductionRequest = z.infer<typeof SpeedupProductionRequestSchema>;

/**
 * POST /api/production/speedup response — Phase 2.D.
 * Returns the post-speedup job snapshot, the refreshed wallet (so the client
 * never has to chase a second round-trip to redraw the diamond chip), and the
 * diamond cost that landed (server-computed, source of truth).
 */
export const SpeedupProductionResponseSchema = z.object({
  ok: z.literal(true),
  job: ProductionJobSchema,
  wallet: PlayerWalletSchema,
  costDiamonds: z.number().int().nonnegative(),
});
export type SpeedupProductionResponse = z.infer<typeof SpeedupProductionResponseSchema>;

/** GET /api/production/list?baseId= */
export const ProductionListQuerySchema = z.object({
  baseId: z.string().uuid(),
});
export type ProductionListQuery = z.infer<typeof ProductionListQuerySchema>;

export const ProductionListResponseSchema = z.object({
  baseId: z.string().uuid(),
  serverNowMs: z.number().int().nonnegative(),
  jobs: z.array(ProductionJobSchema),
});
export type ProductionListResponse = z.infer<typeof ProductionListResponseSchema>;

/* ── Phase 2.A: claim flow ──────────────────────────────────────────────── */

/** POST /api/production/claim — settle a single complete job. */
export const ClaimProductionRequestSchema = z.object({
  jobId: z.string().uuid(),
});
export type ClaimProductionRequest = z.infer<typeof ClaimProductionRequestSchema>;

/**
 * POST /api/production/claim-all — settle every complete job for a base
 * (and optionally narrow to one building when the in-scene marker is tapped).
 */
export const ClaimAllProductionRequestSchema = z.object({
  baseId: z.string().uuid(),
  baseBuildingId: z.string().uuid().optional(),
});
export type ClaimAllProductionRequest = z.infer<typeof ClaimAllProductionRequestSchema>;

/** Common response shape for both claim endpoints. */
export const ClaimProductionResponseSchema = z.object({
  ok: z.literal(true),
  claimed: z.array(ProductionJobSchema),
  wallet: PlayerWalletSchema,
  /** Whole inventory rows that bumped during the claim batch. */
  inventoryDelta: z.array(InventoryItemSchema),
});
export type ClaimProductionResponse = z.infer<typeof ClaimProductionResponseSchema>;
